import { useEffect, useMemo, useState } from 'react';
import {
    Button, ButtonGroup, FormGroup, Label, Input, InputGroup,
    InputGroupText, Spinner, Alert,
} from 'reactstrap';
import { BiPlus, BiTrash, BiInfoCircle } from 'react-icons/bi';
import useJobsDropdown from '../../hooks/useJobsDropdown';
import { currencySymbol } from '../../constants/currencies';

// Two-decimal rounding cap mirrors ExpenseSplitsHelper.SumTolerance on the
// server. Anything beyond £0.01 of drift is real input that needs rebalancing.
const TOLERANCE = 0.01;

const round2 = (n) => Math.round(n * 100) / 100;

// Empty editor state — caller passes `splits=null` to mean "no splits".
// We always have at least one row in local state so the user can fill it in;
// when they click "Add another job" we go to two rows and the split is "real".
const blankRow = () => ({
    id: null,           // server id (null on create)
    jobId: '',          // string for <select> binding; '' = unselected
    amount: '',         // user-typed amount (string for free editing)
    percent: '',        // user-typed percent (string)
    vatAmount: '',      // user-typed vat (string; '' = "no per-row VAT")
    _key: Math.random().toString(36).slice(2),
});

// ExpenseSplitEditor
// ──────────────────
// Controlled component. Parent holds split state (an array of row objects)
// and the editor mutates it via onChange. Parent is responsible for sending
// the final shape to the API on save.
//
// Inputs:
//   value              — array of split rows (mirrors row shape above) or null
//   onChange(next)     — called with the next array
//   primaryJobId       — the expense's primary JobId. Always seeded as the
//                        first row when splits is non-empty (server validation).
//   amount             — current expense.Amount (number)
//   vatAmount          — current expense.VatAmount (number or null)
//   currency           — expense currency (drives the £/€/$ prefix and which
//                        jobs are pickable)
//   disabled           — disable all inputs (e.g. while saving)
//
// Output value shape (when non-empty):
//   [{ id, jobId, amount, percent, vatAmount }, ...]
// where the parent must convert to API shape on submit.
//
// Modes:
//   'amount'  — user enters £ allocations directly. Percentages are derived.
//   'percent' — user enters %. Amounts are derived from `amount` prop.
//
// The editor never silently rebalances on blur. The user owns the numbers.
// Three "fix it for me" buttons offer one-click resolution:
//   - Match total to splits: bumps `amount` to sum(allocations) via onAmountChange
//   - Rebalance splits to total: scales each row to sum to `amount`
//   - Split evenly: divides `amount` equally across rows
export default function ExpenseSplitEditor({
    value,
    onChange,
    primaryJobId,
    amount,
    vatAmount,
    currency = 'GBP',
    disabled = false,
    onAmountChange,
}) {
    const jobs = useJobsDropdown();
    const symbol = currencySymbol(currency);

    const [mode, setMode] = useState('amount');
    const [showVat, setShowVat] = useState(() =>
        Array.isArray(value) && value.some(r => r.vatAmount !== '' && r.vatAmount != null)
    );

    // Index jobs by id for fast lookup in row labels.
    const jobsById = useMemo(() => {
        const m = new Map();
        for (const j of (jobs.data ?? [])) m.set(j.id, j);
        return m;
    }, [jobs.data]);

    // Candidate split-target jobs. A split allocates a share of THIS expense
    // (in the expense's own currency) to another job; the server converts each
    // allocation into the target job's currency at the transaction-date rate
    // when rolling up its totals. So target jobs no longer have to match the
    // expense currency — any job can participate.
    const pickableJobs = jobs.data ?? [];

    // Whether splits is "active" — i.e. has 2+ rows. Single row means the
    // editor is collapsed to its idle "Split this expense across jobs?" CTA.
    const isActive = Array.isArray(value) && value.length >= 2;

    const rows = isActive ? value : [];

    const totalAmount = Number(amount) || 0;
    const sumAllocated = round2(rows.reduce((a, r) => a + (Number(r.amount) || 0), 0));
    const sumPercent   = round2(rows.reduce((a, r) => a + (Number(r.percent) || 0), 0));
    const balanced     = Math.abs(sumAllocated - totalAmount) <= TOLERANCE;

    const sumVat       = round2(rows.reduce((a, r) => a + (Number(r.vatAmount) || 0), 0));
    const totalVat     = Number(vatAmount) || 0;
    const vatBalanced  = !showVat || Math.abs(sumVat - totalVat) <= TOLERANCE;

    // ── Activation / deactivation ─────────────────────────────────────

    const activate = () => {
        if (disabled) return;
        // Seed with the primary job + a blank second row. Even split by default.
        const half = round2(totalAmount / 2);
        const remainder = round2(totalAmount - half);
        onChange([
            { ...blankRow(), jobId: primaryJobId ? String(primaryJobId) : '', amount: half ? String(half.toFixed(2)) : '', percent: totalAmount > 0 ? '50' : '' },
            { ...blankRow(),                                                  amount: remainder ? String(remainder.toFixed(2)) : '', percent: totalAmount > 0 ? '50' : '' },
        ]);
    };

    const deactivate = () => {
        if (disabled) return;
        onChange([]);
    };

    // ── Row-level edits ───────────────────────────────────────────────

    const updateRow = (idx, patch) => {
        const next = rows.map((r, i) => i === idx ? { ...r, ...patch } : r);
        onChange(next);
    };

    const setRowAmount = (idx, raw) => {
        const next = raw;
        // When the user types in amount mode, recompute the row's % (display only).
        const derivedPct = totalAmount > 0
            ? String(round2((Number(next) || 0) / totalAmount * 100))
            : '';
        updateRow(idx, { amount: next, percent: derivedPct });
    };

    const setRowPercent = (idx, raw) => {
        const next = raw;
        // % mode → derive amount from total.
        const derivedAmt = totalAmount > 0
            ? String(round2((Number(next) || 0) / 100 * totalAmount).toFixed(2))
            : '';
        updateRow(idx, { percent: next, amount: derivedAmt });
    };

    const setRowVat = (idx, raw) => updateRow(idx, { vatAmount: raw });
    const setRowJob = (idx, raw) => updateRow(idx, { jobId: raw });

    const addRow = () => onChange([...rows, blankRow()]);

    const removeRow = (idx) => {
        const next = rows.filter((_, i) => i !== idx);
        // Going below 2 rows = collapse back to single-job. Server requires
        // 2+ rows when splits are present, so anything else is meaningless.
        if (next.length < 2) onChange([]);
        else onChange(next);
    };

    // ── Quick-fix actions ─────────────────────────────────────────────

    const matchTotalToSplits = () => {
        if (!onAmountChange) return;
        onAmountChange(sumAllocated.toFixed(2));
    };

    const rebalanceToTotal = () => {
        if (totalAmount <= 0 || sumAllocated <= 0) return;
        const factor = totalAmount / sumAllocated;
        // Scale every row by the factor, then push any rounding remainder
        // onto the largest row so the sum lands exactly on totalAmount.
        const scaled = rows.map(r => ({ ...r, amount: round2((Number(r.amount) || 0) * factor) }));
        const sum = round2(scaled.reduce((a, r) => a + r.amount, 0));
        const drift = round2(totalAmount - sum);
        if (Math.abs(drift) > 0) {
            let largestIdx = 0;
            for (let i = 1; i < scaled.length; i++)
                if (scaled[i].amount > scaled[largestIdx].amount) largestIdx = i;
            scaled[largestIdx].amount = round2(scaled[largestIdx].amount + drift);
        }
        onChange(scaled.map(r => ({
            ...r,
            amount: r.amount.toFixed(2),
            percent: totalAmount > 0 ? String(round2(r.amount / totalAmount * 100)) : '',
        })));
    };

    const splitEvenly = () => {
        if (totalAmount <= 0 || rows.length === 0) return;
        const each = round2(totalAmount / rows.length);
        const last = round2(totalAmount - each * (rows.length - 1));
        const evenPct = round2(100 / rows.length);
        const next = rows.map((r, i) => ({
            ...r,
            amount: (i === rows.length - 1 ? last : each).toFixed(2),
            percent: String(evenPct),
        }));
        onChange(next);
    };

    // Sync rows when amount changes externally (user editing the main Amount
    // field while splits are active). Re-derive % column from new total. We
    // deliberately DO NOT touch row amounts — the user owns those numbers.
    useEffect(() => {
        if (!isActive) return;
        const updated = rows.map(r => ({
            ...r,
            percent: totalAmount > 0
                ? String(round2((Number(r.amount) || 0) / totalAmount * 100))
                : '',
        }));
        // Only push if any % changed (avoid render loops).
        const changed = updated.some((r, i) => r.percent !== rows[i].percent);
        if (changed) onChange(updated);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalAmount]);

    // ── Render ────────────────────────────────────────────────────────

    if (!isActive) {
        return (
            <div className="border rounded p-3 mb-3 bg-body-tertiary">
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <div>
                        <div className="fw-semibold small">Split this expense across multiple jobs?</div>
                        <div className="text-muted small">
                            Useful for material runs covering several jobs at once.
                        </div>
                    </div>
                    <Button color="primary" outline size="sm" onClick={activate} disabled={disabled}>
                        Split expense
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="border rounded p-3 mb-3">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                <div>
                    <div className="fw-semibold">Split across jobs</div>
                    <div className="text-muted small">
                        Each row's allocation must sum to the expense total ({symbol}{totalAmount.toFixed(2)}).
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <ButtonGroup size="sm">
                        <Button color="secondary" outline={mode !== 'amount'} active={mode === 'amount'}
                                onClick={() => setMode('amount')}>By amount</Button>
                        <Button color="secondary" outline={mode !== 'percent'} active={mode === 'percent'}
                                onClick={() => setMode('percent')}>By percent</Button>
                    </ButtonGroup>
                    <Button color="link" size="sm" onClick={deactivate}
                            disabled={disabled} className="text-decoration-none">
                        Remove split
                    </Button>
                </div>
            </div>

            {jobs.isLoading ? (
                <div className="text-center py-3"><Spinner size="sm" /></div>
            ) : (
                <>
                    {rows.map((row, idx) => {
                        const job = row.jobId ? jobsById.get(Number(row.jobId)) : null;
                        // The row whose jobId matches the primary is locked —
                        // the primary is already chosen by the surrounding form
                        // and re-picking it here would be a confusing duplicate.
                        const isPrimary = primaryJobId != null
                            && row.jobId !== ''
                            && Number(row.jobId) === Number(primaryJobId);
                        // Hide jobs already picked on other rows AND the primary
                        // (it's implicitly taken by its locked row).
                        const otherPickedIds = new Set(
                            rows
                                .map((r, j) => (j !== idx && r.jobId !== '' ? Number(r.jobId) : null))
                                .filter(id => id != null)
                        );
                        if (primaryJobId != null) otherPickedIds.add(Number(primaryJobId));
                        const rowPickable = pickableJobs.filter(j => !otherPickedIds.has(j.id));
                        const primaryLabel = isPrimary && job
                            ? `${job.jobNumber ? `${job.jobNumber} · ` : ''}${job.title || '(untitled)'}${job.customerName ? ` — ${job.customerName}` : ''}`
                            : null;

                        return (
                            <div key={row._key} className="border rounded bg-body-tertiary p-2 mb-2">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    {isPrimary ? (
                                        <div className="flex-grow-1 d-flex align-items-center gap-2 px-2 py-1 border rounded bg-body">
                                            <span className="badge bg-secondary-subtle text-secondary-emphasis flex-shrink-0">
                                                Primary
                                            </span>
                                            <span className="small text-truncate" title={primaryLabel ?? ''}>
                                                {primaryLabel ?? 'Primary job'}
                                            </span>
                                        </div>
                                    ) : (
                                        <Input type="select" bsSize="sm" value={row.jobId}
                                               onChange={(e) => setRowJob(idx, e.target.value)}
                                               disabled={disabled}
                                               className="flex-grow-1">
                                            <option value="">— Pick a job —</option>
                                            {rowPickable.map(j => (
                                                <option key={j.id} value={j.id}>
                                                    {j.jobNumber ? `${j.jobNumber} · ` : ''}{j.title || '(untitled)'}{j.customerName ? ` — ${j.customerName}` : ''}
                                                </option>
                                            ))}
                                        </Input>
                                    )}
                                    {/* Trash hidden on the primary row — the
                                        primary can't be detached without
                                        collapsing the whole split (use Remove
                                        split at the top for that). */}
                                    {!isPrimary && (
                                        <Button color="danger" outline size="sm"
                                                onClick={() => removeRow(idx)}
                                                disabled={disabled}
                                                title="Remove this row"
                                                className="flex-shrink-0">
                                            <BiTrash />
                                        </Button>
                                    )}
                                </div>
                                {!isPrimary && row.jobId && !job && (
                                    <div className="text-muted small mb-2">Job not found.</div>
                                )}
                                <div className="d-flex gap-2">
                                    {mode === 'amount' ? (
                                        <InputGroup size="sm" className="flex-grow-1">
                                            <InputGroupText>{symbol}</InputGroupText>
                                            <Input type="number" step="0.01" min="0"
                                                   value={row.amount}
                                                   onChange={(e) => setRowAmount(idx, e.target.value)}
                                                   disabled={disabled}
                                                   placeholder="0.00" />
                                        </InputGroup>
                                    ) : (
                                        <InputGroup size="sm" className="flex-grow-1">
                                            <Input type="number" step="0.1" min="0" max="100"
                                                   value={row.percent}
                                                   onChange={(e) => setRowPercent(idx, e.target.value)}
                                                   disabled={disabled}
                                                   placeholder="0" />
                                            <InputGroupText>%</InputGroupText>
                                        </InputGroup>
                                    )}
                                    {showVat && (
                                        <InputGroup size="sm" className="flex-grow-1">
                                            <InputGroupText>{symbol}</InputGroupText>
                                            <Input type="number" step="0.01" min="0"
                                                   value={row.vatAmount}
                                                   onChange={(e) => setRowVat(idx, e.target.value)}
                                                   disabled={disabled}
                                                   placeholder="VAT" />
                                        </InputGroup>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    <Button color="secondary" outline size="sm" onClick={addRow} disabled={disabled}
                            className="d-inline-flex align-items-center gap-1 mt-2">
                        <BiPlus /> Add another job
                    </Button>
                    <div className="form-check form-switch mt-2">
                        <input className="form-check-input" type="checkbox"
                               id="split-vat-toggle"
                               checked={showVat}
                               disabled={disabled}
                               onChange={(e) => setShowVat(e.target.checked)} />
                        <label className="form-check-label small" htmlFor="split-vat-toggle">
                            Per-row VAT (otherwise distributed proportionally)
                        </label>
                    </div>

                    {/* ── Balance summary + quick-fix bar ── */}
                    <div className="border-top mt-3 pt-3">
                        <div className="d-flex justify-content-between align-items-center small">
                            <div>
                                <span className="text-muted">Allocated:</span>{' '}
                                <strong className={balanced ? 'text-success' : 'text-danger'}>
                                    {symbol}{sumAllocated.toFixed(2)}
                                </strong>
                                <span className="text-muted"> / {symbol}{totalAmount.toFixed(2)}</span>
                                {mode === 'percent' && (
                                    <span className="ms-3 text-muted">({sumPercent.toFixed(1)}%)</span>
                                )}
                            </div>
                            {!balanced && (
                                <span className="text-danger">
                                    {sumAllocated > totalAmount ? 'Over' : 'Under'} by {symbol}
                                    {Math.abs(sumAllocated - totalAmount).toFixed(2)}
                                </span>
                            )}
                        </div>

                        {!balanced && (
                            <div className="d-flex gap-2 mt-2 flex-wrap">
                                {onAmountChange && (
                                    <Button color="primary" outline size="sm" onClick={matchTotalToSplits}
                                            disabled={disabled || sumAllocated <= 0}>
                                        Match total to splits ({symbol}{sumAllocated.toFixed(2)})
                                    </Button>
                                )}
                                <Button color="primary" outline size="sm" onClick={rebalanceToTotal}
                                        disabled={disabled || totalAmount <= 0 || sumAllocated <= 0}>
                                    Rebalance splits to total
                                </Button>
                                <Button color="primary" outline size="sm" onClick={splitEvenly}
                                        disabled={disabled || totalAmount <= 0}>
                                    Split evenly
                                </Button>
                            </div>
                        )}

                        {showVat && !vatBalanced && (
                            <Alert color="warning" className="mt-2 mb-0 py-2 small d-flex align-items-start gap-2">
                                <BiInfoCircle className="mt-1 flex-shrink-0" />
                                <div>
                                    Per-row VAT sums to {symbol}{sumVat.toFixed(2)} but the expense VAT total
                                    is {symbol}{totalVat.toFixed(2)}. Adjust the rows or untick the per-row VAT
                                    toggle to let the server distribute proportionally.
                                </div>
                            </Alert>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

// Helper: convert the editor's local row shape into the API payload shape.
// Returns null when the editor is collapsed (no splits) so callers can pass
// it through to the API as `splits: null` (= leave existing splits alone on
// update, or no splits on create).
export function splitsToPayload(rows) {
    if (!Array.isArray(rows) || rows.length < 2) return null;
    return rows.map(r => ({
        jobId: r.jobId === '' ? 0 : Number(r.jobId),
        allocatedAmount: Number(r.amount) || 0,
        // '' / null → omit (server fills proportionally). 0 is a valid value.
        allocatedVatAmount: r.vatAmount === '' || r.vatAmount == null ? null : Number(r.vatAmount),
    }));
}

// Helper: detect whether any row is missing a required field. Surfaces on
// the parent's submit button so we can keep the gating logic in one place.
export function splitsHaveErrors(rows, totalAmount) {
    if (!Array.isArray(rows) || rows.length === 0) return false; // not splitting
    if (rows.length < 2) return true;
    const seen = new Set();
    let sum = 0;
    for (const r of rows) {
        if (!r.jobId) return true;
        if (seen.has(r.jobId)) return true;
        seen.add(r.jobId);
        const amt = Number(r.amount);
        if (Number.isNaN(amt) || amt < 0) return true;
        sum += amt;
    }
    return Math.abs(round2(sum) - (Number(totalAmount) || 0)) > TOLERANCE;
}

// Helper: hydrate the editor from a server-returned splits[] array.
// Used by ExpenseDetailModal when opening an existing expense.
export function splitsFromServer(serverSplits, totalAmount) {
    if (!Array.isArray(serverSplits) || serverSplits.length === 0) return [];
    const total = Number(totalAmount) || 0;
    return serverSplits.map(s => ({
        id: s.id ?? null,
        jobId: s.jobId != null ? String(s.jobId) : '',
        amount: s.allocatedAmount != null ? Number(s.allocatedAmount).toFixed(2) : '',
        percent: total > 0 && s.allocatedAmount != null
            ? String(round2(s.allocatedAmount / total * 100))
            : '',
        vatAmount: s.allocatedVatAmount != null ? Number(s.allocatedVatAmount).toFixed(2) : '',
        _key: Math.random().toString(36).slice(2),
    }));
}
